import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass, faCalendarDays, faShieldHalved, faBriefcase, 
} from '@fortawesome/free-solid-svg-icons';
import { ecourtApi } from './ecourtApi';
import { ecPath, normalizeCnr, CNR_RE, fmtDate } from './ecourtUtils';
import { BalancePill } from './EcourtUi';

const TOOLS = [
  { to: 'my-cases', icon: faBriefcase, tone: 'yellow', title: 'My Cases', text: 'Your saved cases in one place — next hearing, status and what changed since you last looked.' },
  { to: 'search', icon: faMagnifyingGlass, tone: 'blue', title: 'Case Search', text: 'Party, advocate, judge, keyword or section across the Supreme Court, High Courts, district courts and tribunals.' },
  { to: 'causelist', icon: faCalendarDays, tone: 'green', title: 'Cause List', text: 'Daily board by court, judge or advocate, or check if your cases are listed.' },
  { to: 'legal-check', icon: faShieldHalved, tone: 'red', title: 'Legal Check', text: 'Litigation background report on a person or company, with a risk score.' },
];

export default function EcourtHome() {
  const navigate = useNavigate();
  const [cnr, setCnr] = useState('');
  const [err, setErr] = useState('');
  const [mine, setMine] = useState(null);

  useEffect(() => { ecourtApi.myCases().then((r) => setMine(r.cases || [])).catch(() => setMine([])); }, []);

  const open = (e) => {
    e.preventDefault();
    const c = normalizeCnr(cnr);
    if (!CNR_RE.test(c)) {
      setErr('A CNR has 16 characters: 4 letters then 12 digits, e.g. TNCB010012342025.');
      return;
    }
    navigate(ecPath(`case/${c}`));
  };

  const upcoming = (mine || []).filter((c) => c.next_hearing_date).slice(0, 3);
  const newUpdates = (mine || []).reduce((n, c) => n + (c.new_updates || 0), 0);

  return (
    <div className="ec-home">
      <div className="text-center mb-4">
        <h1 className="ec-hero-title">eCourts</h1>
        <p className="ec-muted mb-2">Case status, orders and cause lists from every Indian court.</p>
        <BalancePill />
      </div>

      <form className="ec-cnr-box mx-auto mb-4" onSubmit={open} noValidate>
        <label htmlFor="ec-home-cnr" className="ec-label">Open a case by CNR</label>
        <div className="d-flex gap-2">
          <input id="ec-home-cnr" className="form-control ec-mono" value={cnr} maxLength={20}
            placeholder="TNCB010012342025" onChange={(e) => { setCnr(e.target.value); setErr(''); }}
            aria-invalid={Boolean(err)} aria-describedby="ec-home-cnr-err" />
          <button type="submit" className="btn btn-ec">Open</button>
        </div>
        {err && <div id="ec-home-cnr-err" className="ec-field-error">{err}</div>}
      </form>

      {mine && mine.length > 0 && (
        <Link to={ecPath('my-cases')} className="ec-card ec-card-tight d-flex flex-wrap align-items-center gap-3 mb-4 ec-link-plain ec-mycases-strip">
          <strong>My Cases · {mine.length}</strong>
          {newUpdates > 0 && <span className="ec-badge ec-badge-yellow">{newUpdates} new update{newUpdates > 1 ? 's' : ''}</span>}
          {upcoming.map((c) => (
            <span key={c.id} className="small ec-muted">{fmtDate(c.next_hearing_date)} · {c.label || c.title || c.cnr}</span>
          ))}
        </Link>
      )}

      <div className="row g-3 pt-1">
        {TOOLS.map((t) => (
          <div className="col-12 col-sm-6 col-xl-6" key={t.to}>
            <Link to={ecPath(t.to)} className="ec-tool">
              <span className={`ec-tool-icon tone-${t.tone}`}><FontAwesomeIcon icon={t.icon} /></span>
              <span className="ec-tool-title">{t.title}</span>
              <span className="ec-tool-text">{t.text}</span>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}